import { useEffect, useState } from 'react';
import { ProjectFolderArt } from '../../ui/ProjectFolderArt';
import type { ProjectInfo } from '../../home/rows';
import { listMedia } from '../../services/library';
import { tip } from '../../ui/tooltip';
import './projectFolders.css';

/** Photo pocket folders, neutral like the Home's: the project's cover in front, then its Drive previews. Never a colour. */
type Folder = Pick<ProjectInfo, 'id' | 'name' | 'cover'>;

export function ProjectFolders({ projects, onOpen }: { projects: readonly Folder[]; onOpen: (project: Folder) => void }) {
  const [covers, setCovers] = useState<Record<string, string[]>>({});
  useEffect(() => {
    let live = true;
    const queue = [...projects];
    const readNext = async () => {
      while (live && queue.length) {
        const project = queue.shift()!;
        const pics = await listMedia({ place: { project: project.id } })
          .then(page => [...new Set(page.items.map(item => item.thumb))].slice(0, 3))
          .catch(() => []);
        if (live) setCovers(previous => ({ ...previous, [project.id]: pics }));
      }
    };
    const workers = Math.min(3, queue.length);
    for (let index = 0; index < workers; index += 1) void readNext();
    return () => { live = false; };
  }, [projects]);
  return <div className="artwork-project-folders" role="group" aria-label="Projects">
    {projects.map(project => <ProjectFolder key={project.id} name={project.name} pics={project.cover ? [project.cover, ...(covers[project.id] ?? [])].slice(0, 3) : covers[project.id]} onOpen={() => onOpen(project)} />)}
  </div>;
}

type FolderProps = { name: string; pics?: readonly string[]; onOpen: () => void };

function ProjectFolder({ name, pics = [], onOpen }: FolderProps) {
  return (
    <button type="button" className="artwork-project-folder" data-project-folder onClick={onOpen} aria-label={"Open project " + name} {...tip(name)}>
      <ProjectFolderArt covers={pics} />
      <span className="artwork-project-folder-name">{name}</span>
    </button>
  );
}
